import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { getTeacherAssignments, getStudentAssignments } from '../../services/assignmentService';
import halaqaService from '../../services/halaqaService';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import '../../styles/AssignmentsListPage.css';

const AssignmentsListPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [teacherHalaqa, setTeacherHalaqa] = useState(null);
  const [displayLimit, setDisplayLimit] = useState(10); // Show 10 items initially
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [localStudentName, setLocalStudentName] = useState('');
  
  // Get studentId from URL params first, then fall back to location state
  const studentId = params.studentId || location.state?.studentId;
  const studentName = location.state?.studentName;

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      
      let data;
      
      // If student role, get their own assignments
      if (user?.role === 'student') {
        // Get the logged-in student's assignments
        data = await getStudentAssignments();
        console.log('Fetched assignments for logged-in student:', data);
      }
      // If we have a studentId from params (teacher viewing student), use the student-specific endpoint
      else if (studentId) {
        data = await getStudentAssignments(studentId);
        console.log(`Fetched assignments for student ${studentId}:`, data);
      } else {
        // Otherwise, get all teacher assignments
        data = await getTeacherAssignments(
          user?.center, 
          teacherHalaqa ? teacherHalaqa._id : null
        );
        console.log('Fetched teacher assignments:', data);
      }
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setAssignments(data);
      } else if (data && Array.isArray(data.data)) {
        // Handle potential API response structure
        setAssignments(data.data);
      } else {
        console.error('Expected array of assignments but got:', data);
        setAssignments([]);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError('Failed to load assignments. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherHalaqa = async () => {
    try {
      const halaqas = await halaqaService.getAssignedHalaqas();
      if (halaqas && halaqas.length > 0) {
        setTeacherHalaqa(halaqas[0]);
      }
    } catch (error) {
      console.error('Error fetching teacher halaqa:', error);
    }
  };

  useEffect(() => {
    // Call both functions
    fetchAssignments();
    fetchTeacherHalaqa();
  }, []);
  
  // Separate effect for fetching student name to avoid dependency issues
  useEffect(() => {
    // If we're viewing a specific student's assignments but don't have the name
    if (studentId && !studentName) {
      const fetchStudentName = async () => {
        try {
          // Try to get the student name from the API
          const response = await api.get(`/users/${studentId}`);
          if (response.data && response.data.name) {
            // Store it in local state since we can't update location.state
            setLocalStudentName(response.data.name);
          }
        } catch (error) {
          console.error('Error fetching student name:', error);
        }
      };
      
      fetchStudentName();
    }
  }, [studentId, studentName]);
  
  // Handle scroll events to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Function to scroll back to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const editAssignment = (item, e) => {
    e.preventDefault(); // Prevent the parent Link from navigating
    console.log('Editing assignment:', item._id);
    // Match the route path in App.js: /assignments/:id/edit
    navigate(`/assignments/${item._id}/edit`, {
      state: {
        isEditMode: true,
        assignmentId: item._id,
        halaqaId: teacherHalaqa ? teacherHalaqa._id : null
      }
    });
  };

  const navigateToAssessment = (item, e) => {
    e.preventDefault(); // Prevent the parent Link from navigating
    if (item.students && item.students.length > 0) {
      // Navigate to assessment screen
      navigate(`/assignments/${item._id}/assess`, {
        state: {
          studentId: item.students[0]._id,
          studentName: item.students[0].name,
          assignmentDetails: `${item.surah.name} (${item.verses.from}-${item.verses.to})`,
          centerId: item.center
        }
      });
    } else {
      window.alert('No students assigned to this assignment');
    }
  };

  const filterAssignments = (filter) => {
    setActiveFilter(filter);
  };

  const getFilteredAssignments = () => {
    if (activeFilter === 'all') return assignments;
    
    if (activeFilter === 'pending') {
      return assignments.filter(assignment => 
        assignment.status === 'active' && 
        (!assignment.assessment || !assignment.assessment.rating)
      );
    }
    
    if (activeFilter === 'assessed') {
      return assignments.filter(assignment => 
        assignment.assessment && assignment.assessment.rating
      );
    }
    
    if (activeFilter === 'new-memorization' || activeFilter === 'revision') {
      return assignments.filter(assignment => 
        assignment.type === activeFilter
      );
    }
    
    return assignments;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const renderAssignmentCard = (item) => {
    // Add fallback handling for missing data in the assignment object
    if (!item || !item.surah || !item.verses) {
      console.error('Invalid assignment data:', item);
      return null;
    }

    const isAssessed = item.assessment && item.assessment.rating;
    
    // Ultra basic card layout that mirrors what's shown in Revision tab
    return (
      <div className="simple-assignment-card" key={item._id}>
        <div className="simple-assignment-header">
          <h3 className="simple-assignment-title">
            {`${item.surah.name || 'Unknown Surah'} (${item.verses.from || '?'}-${item.verses.to || '?'})`}
          </h3>
          <div className="simple-action-buttons">
            <a href="#" 
              onClick={(e) => {
                e.preventDefault();
                navigate(`/assignments/${item._id}/edit`, {
                  state: { isEditMode: true, assignmentId: item._id }
                });
              }} 
              className="simple-edit-btn">
              Edit
            </a>
            
            <a href="#" 
              onClick={(e) => {
                e.preventDefault();
                if (item.students && item.students.length > 0) {
                  navigate(`/assignments/${item._id}/assess`, {
                    state: {
                      studentId: item.students[0]._id,
                      studentName: item.students[0].name,
                      assignmentDetails: `${item.surah.name} (${item.verses.from}-${item.verses.to})`
                    }
                  });
                } else {
                  window.alert('No students assigned to this assignment');
                }
              }} 
              className="simple-assess-btn">
              {isAssessed ? 'View' : 'Assess'}
            </a>
          </div>
        </div>
        
        <div className="simple-assignment-details">
          {studentId ? 
            // When viewing a specific student's assignments, use the studentName from params or our local state
            `Assigned to: ${studentName || localStudentName || 'Student'}` 
            : 
            // Normal case - display students from the assignment
            `Assigned to: ${Array.isArray(item.students) ? 
              item.students.map(s => typeof s === 'object' ? s.name : s).join(', ') : 'No students'}`
          }
        </div>
        
        <div className="simple-assignment-footer">
          <div className="simple-due-date">
            Due: {formatDate(item.dueDate)}
          </div>
          <div className="simple-status">
            {item.status === 'active' ? 'Active' : item.status}
          </div>
          <div className="simple-type">
            {item.type === 'new-memorization' ? 'New Memorization' : 'Revision'}
          </div>
          {isAssessed && (
            <div className="simple-assessment">
              {item.assessment.rating}
            </div>
          )}
        </div>
      </div>
    );
  };

  const canCreateAssignment = () => {
    return user && (user.role === 'admin' || user.role === 'teacher' || user.role === 'super_admin');
  };

  return (
    <div className="assignments-list-container" id="assignments-container">
      <div className="header">
        <h1>{studentId ? `${studentName || localStudentName || 'Student'}'s Assignments` : 'Assignments'}</h1>
        <div className="header-actions">
          {studentId ? (
            location.state?.fromParentChildView ? (
              <Link to={`/parent/children/${studentId}`} className="back-link">
                ← Back to Child Profile
              </Link>
            ) : (
              <Link to={`/students/${studentId}`} className="back-link">
                ← Back to Student Profile
              </Link>
            )
          ) : (
            <Link to="/dashboard" className="back-link">
              ← Back to Dashboard
            </Link>
          )}
   
        
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-chips">
          <button 
            className={`filter-chip ${activeFilter === 'all' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('all')}
          >
            All
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'pending' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('pending')}
          >
            Pending
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'assessed' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('assessed')}
          >
            Assessed
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'new-memorization' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('new-memorization')}
            data-filter-type="new-memorization"
          >
            New Memorization
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'revision' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('revision')}
            data-filter-type="revision"
          >
            Revision
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading assignments...</div>
        </div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="assignments-list">
          {getFilteredAssignments().length > 0 ? (
            <>
              {/* Show how many assignments were found */}
              <div className="assignments-count">
                Found {getFilteredAssignments().length} assignments
                {getFilteredAssignments().length > displayLimit && 
                  <span className="assignments-showing"> (showing {Math.min(displayLimit, getFilteredAssignments().length)})</span>
                }
              </div>
              {getFilteredAssignments().slice(0, displayLimit).map((item) => (
                <div key={item._id} className="assignment-wrapper">
                  {renderAssignmentCard(item)}
                </div>
              ))}
              
              {/* Load more button */}
              {getFilteredAssignments().length > displayLimit && (
                <button 
                  className="load-more-btn"
                  onClick={() => setDisplayLimit(prev => prev + 10)}
                >
                  Load more ({getFilteredAssignments().length - displayLimit} remaining)
                </button>
              )}
              
              {/* Add an extra spacer at the bottom */}
              <div className="list-end-spacer"></div>
              
              {/* Scroll to top button */}
              {showScrollTop && (
                <button 
                  className="scroll-top-btn" 
                  onClick={scrollToTop}
                  aria-label="Scroll to top"
                >
                  ↑ Top
                </button>
              )}
            </>
          ) : (
            <div className="empty-container">
              <p className="empty-text">No assignments found</p>
              {canCreateAssignment() && (
                <Link to="/assignments/new" className="create-assignment-link">
                  Create a new assignment
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssignmentsListPage;